import { useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../lib/api/axios';
import { PaginatedDocumentsResponse, SortField } from 'src/features/chat/types';

export const DEFAULT_PAGE_SIZE = 15;

const DOCUMENT_FETCH_META = {
  globalError: true,
  context: 'documents-fetch',
  description: 'We failed to fetch documents from our database. Please contact support if the problem persists.',
  title: 'Failed to load document data.'
};

async function fetchDocumentsPage(params: URLSearchParams): Promise<PaginatedDocumentsResponse> {
  const response = await apiGet<PaginatedDocumentsResponse>(`/docs/list?${params.toString()}`, {
    meta: DOCUMENT_FETCH_META
  });
  return response.data;
}

function useDebouncedSearch(search: string) {
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  return debouncedSearch;
}

interface DocumentListQuery {
  page: number;
  pageSize: number;
  search: string;
  sortBy: SortField | null;
  reverse: boolean;
  categories: string[];
  domains: string[];
  subdomains: string[];
  authorities: string[];
  documentIds: string[];
  scraperIds: string[];
  favouritesOnly: boolean;
  sources: string[];
  restrictToManaged: boolean;
  publicOnly: boolean;
}

function buildDocumentListParams(query: DocumentListQuery): URLSearchParams {
  const params = new URLSearchParams();
  params.set('page_size', String(query.pageSize));
  params.set('page', String(query.page));
  if (query.sortBy) {
    params.set('sort_by', query.sortBy);
    params.set('reverse', String(query.reverse));
  }
  if (query.search) {
    params.set('search', query.search);
  }
  query.categories.forEach((cat) => params.append('categories', cat));
  query.domains.forEach((dom) => params.append('domains', dom));
  query.subdomains.forEach((sub) => params.append('subdomains', sub));
  query.authorities.forEach((authority) => params.append('authorities', authority.toUpperCase()));
  query.documentIds.forEach((documentId) => params.append('document_ids', documentId));
  query.scraperIds.forEach((scraperId) => params.append('scraper_ids', scraperId));
  query.sources.forEach((src) => params.append('sources', src));
  if (query.favouritesOnly) {
    params.set('favourites_only', 'true');
  }
  if (query.restrictToManaged) {
    params.set('restrict_to_managed', 'true');
  }
  if (query.publicOnly) {
    params.set('public_only', 'true');
  }
  return params;
}

export interface UseDocumentsOptions {
  pageSize?: number;
  page?: number;
  search?: string;
  sortBy?: SortField | null;
  reverse?: boolean;
  categories?: string[];
  domains?: string[];
  subdomains?: string[];
  authorities?: string[];
  documentIds?: string[];
  scraperIds?: string[];
  favouritesOnly?: boolean;
  sources?: string[];
  restrictToManaged?: boolean;
  publicOnly?: boolean;
  enabled?: boolean;
}

/**
 * Fetches WikiDocuments from the backend with server-side pagination, search, and sorting.
 * This is used by both the Wiki page and the Data Library.
 */
export const useDocuments = (options: UseDocumentsOptions = {}) => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    page = 1,
    search = '',
    sortBy = null,
    reverse = false,
    categories = [],
    domains = [],
    subdomains = [],
    authorities = [],
    documentIds = [],
    scraperIds = [],
    favouritesOnly = false,
    sources = [],
    restrictToManaged = false,
    publicOnly = false,
    enabled = true
  } = options;

  const debouncedSearch = useDebouncedSearch(search);
  const params = buildDocumentListParams({
    page,
    pageSize,
    search: debouncedSearch,
    sortBy,
    reverse,
    categories,
    domains,
    subdomains,
    authorities,
    documentIds,
    scraperIds,
    favouritesOnly,
    sources,
    restrictToManaged,
    publicOnly
  });

  return useQuery({
    queryKey: [
      'documents',
      pageSize,
      page,
      debouncedSearch,
      sortBy,
      reverse,
      categories,
      domains,
      subdomains,
      authorities,
      documentIds,
      scraperIds,
      favouritesOnly,
      sources,
      restrictToManaged,
      publicOnly
    ],
    queryFn: () => fetchDocumentsPage(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    // Keep previous data while fetching new page to prevent scroll jump
    placeholderData: (previousData) => previousData
  });
};

/** Same filters as useDocuments, appending each page as the list scrolls. */
export const useInfiniteDocuments = (options: Omit<UseDocumentsOptions, 'page'> = {}) => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    search = '',
    sortBy = null,
    reverse = false,
    categories = [],
    domains = [],
    subdomains = [],
    authorities = [],
    documentIds = [],
    scraperIds = [],
    favouritesOnly = false,
    sources = [],
    restrictToManaged = false,
    publicOnly = false,
    enabled = true
  } = options;

  const debouncedSearch = useDebouncedSearch(search);

  return useInfiniteQuery({
    queryKey: [
      'documents',
      'infinite',
      pageSize,
      debouncedSearch,
      sortBy,
      reverse,
      categories,
      domains,
      subdomains,
      authorities,
      documentIds,
      scraperIds,
      favouritesOnly,
      sources,
      restrictToManaged,
      publicOnly
    ],
    queryFn: ({ pageParam }) =>
      fetchDocumentsPage(
        buildDocumentListParams({
          page: pageParam,
          pageSize,
          search: debouncedSearch,
          sortBy,
          reverse,
          categories,
          domains,
          subdomains,
          authorities,
          documentIds,
          scraperIds,
          favouritesOnly,
          sources,
          restrictToManaged,
          publicOnly
        })
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.page >= lastPage.total_pages ? undefined : lastPage.page + 1),
    enabled,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false
  });
};
